import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { 
  Upload, 
  Mic, 
  Activity, 
  Lock, 
  Zap, 
  ShieldCheck, 
  Cpu,
  Play,
  Pause,
  FileAudio,
  User,
  Bot,
  StopCircle,
  Trash2,
  Edit
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Input } from '../components/ui/input';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { toast } from 'sonner';
import axios from 'axios';

const VoiceLab = () => {
  const [uploading, setUploading] = useState(false);
  const [files, setFiles] = useState([]);
  const [training, setTraining] = useState(false);
  const [personas, setPersonas] = useState([]);
  const [voiceType, setVoiceType] = useState('cloned'); // 'cloned' or 'synthetic'
  
  // Recording State
  const [isRecording, setIsRecording] = useState(false);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [recordingTime, setRecordingTime] = useState(0);

  // Edit/Delete State
  const [deleteId, setDeleteId] = useState(null);
  const [editPersona, setEditPersona] = useState(null);
  const [editName, setEditName] = useState("");

  const fetchPersonas = useCallback(async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/voice/personas`);
      setPersonas(res.data);
    } catch (err) {
      console.error("Failed to fetch personas");
    }
  }, []);

  useEffect(() => {
    fetchPersonas();
  }, [fetchPersonas]);

  useEffect(() => {
    let interval;
    if (isRecording) {
        interval = setInterval(() => setRecordingTime(prev => prev + 1), 1000);
    } else {
        setRecordingTime(0);
    }
    return () => clearInterval(interval);
  }, [isRecording]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs < 10 ? '0' : ''}${secs}`;
  };

  const handleStartRecording = async () => {
    try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        const recorder = new MediaRecorder(stream);
        const chunks = [];

        recorder.ondataavailable = (e) => chunks.push(e.data);
        recorder.onstop = async () => {
            const blob = new Blob(chunks, { type: 'audio/webm' });
            const file = new File([blob], `recording_${new Date().getTime()}.webm`, { type: 'audio/webm' });
            
            await uploadRecordedFile(file);
        };

        recorder.start();
        setMediaRecorder(recorder);
        setIsRecording(true);
    } catch (err) {
        toast.error("Microphone access denied");
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorder) {
        mediaRecorder.stop();
        setIsRecording(false);
        mediaRecorder.stream.getTracks().forEach(track => track.stop());
    }
  };

  const uploadRecordedFile = async (file) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
        const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/voice/upload`, formData);
        setFiles(prev => [...prev, {
            id: res.data.file_id,
            name: "New Microphone Recording",
            size: res.data.size,
            status: 'uploaded'
        }]);
        toast.success("Recording Uploaded Successfully");
    } catch (err) {
        toast.error("Failed to upload recording");
    }
    setUploading(false);
  };

  const handleFileUpload = async (e) => {
    const newFiles = Array.from(e.target.files);
    if (newFiles.length === 0) return;

    setUploading(true);
    
    for (const file of newFiles) {
        const formData = new FormData();
        formData.append('file', file);
        try {
            const res = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/voice/upload`, formData);
            setFiles(prev => [...prev, {
                id: res.data.file_id,
                name: res.data.original_name,
                size: res.data.size,
                status: 'uploaded'
            }]);
            toast.success(`Uploaded: ${res.data.original_name}`);
        } catch (err) {
            toast.error(`Failed to upload ${file.name}`);
        }
    }
    setUploading(false);
  };

  const startTraining = async () => {
    if (files.length === 0 && voiceType === 'cloned') {
      toast.error("Upload voice samples to clone");
      return;
    }
    
    setTraining(true);
    toast.info("Neural Engine: Initializing Voice Cloning Sequence...");
    
    try {
        await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/voice/train`, {
            files: files.map(f => f.id),
            persona_name: voiceType === 'cloned' ? "Sales Team Clone v1" : "Synthetic Agent Alpha",
            voice_type: voiceType
        });
        
        setTimeout(() => {
            setTraining(false);
            toast.success("Training Complete: New Voice Persona Ready");
            fetchPersonas();
        }, 3000);
        
    } catch (err) {
        toast.error("Training Failed");
        setTraining(false);
    }
  };

  // CRUD Operations
  const confirmDelete = () => {
    if (deleteId) {
        // In real app: await axios.delete(`/api/voice/personas/${deleteId}`)
        setPersonas(prev => prev.filter(p => p.id !== deleteId));
        setDeleteId(null);
        toast.success("Persona deleted successfully");
    }
  };

  const openEdit = (persona) => {
    setEditPersona(persona);
    setEditName(persona.name);
  };

  const confirmEdit = () => {
    if (editPersona) {
        // In real app: await axios.put(`/api/voice/personas/${editPersona.id}`, { name: editName })
        setPersonas(prev => prev.map(p => p.id === editPersona.id ? { ...p, name: editName } : p));
        setEditPersona(null);
        toast.success("Persona updated successfully");
    }
  };

  return (
    <div className="space-y-8 relative">
      {/* Enterprise Watermark */}
      <div className="absolute top-0 right-0 p-4 opacity-10 pointer-events-none select-none">
        <h1 className="text-9xl font-black text-foreground tracking-tighter">ENT</h1>
      </div>

      {/* Header */}
      <div className="flex flex-col gap-2 relative z-10">
        <div className="flex items-center gap-3">
          <h1 className="text-4xl font-bold tracking-tight text-foreground font-heading">Voice Lab</h1>
          <Badge className="bg-gradient-to-r from-amber-400 to-orange-600 text-white border-0 px-3 py-1 shadow-lg shadow-orange-500/20">
            ENTERPRISE EDITION
          </Badge>
        </div>
        <p className="text-muted-foreground text-lg">
          Create High-Fidelity Voice Clones (Team) or Generate Synthetic Personas.
        </p>
      </div>

      <div className="grid lg:grid-cols-12 gap-8">
        {/* Left: Configuration & Upload */}
        <div className="lg:col-span-4 space-y-6">
          <Card className="bg-card/50 border-amber-500/20 shadow-lg shadow-amber-500/5">
            <CardHeader>
              <CardTitle className="text-amber-600">Voice Source</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                    <div 
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${voiceType === 'cloned' ? 'border-amber-500 bg-amber-500/10' : 'border-border bg-card hover:bg-accent'}`}
                        onClick={() => setVoiceType('cloned')}
                    >
                        <div className="flex flex-col items-center gap-2">
                            <User className={`h-6 w-6 ${voiceType === 'cloned' ? 'text-amber-500' : 'text-muted-foreground'}`} />
                            <span className="font-bold text-sm">Clone Human</span>
                        </div>
                    </div>
                    <div 
                        className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${voiceType === 'synthetic' ? 'border-blue-500 bg-blue-500/10' : 'border-border bg-card hover:bg-accent'}`}
                        onClick={() => setVoiceType('synthetic')}
                    >
                        <div className="flex flex-col items-center gap-2">
                            <Bot className={`h-6 w-6 ${voiceType === 'synthetic' ? 'text-blue-500' : 'text-muted-foreground'}`} />
                            <span className="font-bold text-sm">AI Generated</span>
                        </div>
                    </div>
                </div>

                {voiceType === 'cloned' && (
                    <div className="space-y-4">
                        {/* File Upload Area */}
                        <div className="border-2 border-dashed border-border hover:border-amber-500/50 rounded-xl p-6 transition-all bg-muted/20 relative group">
                            <div className="flex flex-col items-center justify-center text-center space-y-3">
                                <div className="h-12 w-12 rounded-full bg-amber-500/10 flex items-center justify-center">
                                    <Upload className="h-6 w-6 text-amber-500" />
                                </div>
                                <div>
                                    <p className="font-medium text-sm">Upload Voice Samples</p>
                                    <p className="text-[10px] text-muted-foreground">MP3/WAV (Min 1 min)</p>
                                </div>
                                <input 
                                    type="file" 
                                    multiple 
                                    accept="audio/*" 
                                    className="absolute inset-0 opacity-0 cursor-pointer"
                                    onChange={handleFileUpload}
                                />
                            </div>
                        </div>

                        {/* Recording Area */}
                        <div className="flex items-center justify-between p-4 bg-muted/30 rounded-lg border border-border">
                            <div className="flex items-center gap-3">
                                <div className={`h-3 w-3 rounded-full ${isRecording ? 'bg-red-500 animate-pulse' : 'bg-slate-400'}`}></div>
                                <span className="text-sm font-medium font-mono">{formatTime(recordingTime)}</span>
                            </div>
                            {!isRecording ? (
                                <Button size="sm" variant="destructive" className="h-8 gap-2" onClick={handleStartRecording}>
                                    <Mic className="h-3 w-3" /> Record
                                </Button>
                            ) : (
                                <Button size="sm" variant="outline" className="h-8 gap-2 border-red-200 text-red-500 hover:bg-red-50" onClick={handleStopRecording}>
                                    <StopCircle className="h-3 w-3" /> Stop
                                </Button>
                            )}
                        </div>
                    </div>
                )}

              {/* File List */}
              <ScrollArea className="h-[200px] pr-4">
                <div className="space-y-2">
                  {files.map((file, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-background border border-border">
                      <div className="flex items-center gap-3">
                        <FileAudio className="h-4 w-4 text-amber-500" />
                        <div className="flex flex-col">
                          <span className="text-sm font-medium truncate max-w-[150px]">{file.name}</span>
                          <span className="text-xs text-muted-foreground">{file.size}</span>
                        </div>
                      </div>
                      <Badge variant="outline" className="text-xs text-green-600 border-green-200 bg-green-50">Ready</Badge>
                    </div>
                  ))}
                </div>
              </ScrollArea>
            </CardContent>
          </Card>
        </div>

        {/* Center: Training Engine */}
        <div className="lg:col-span-5 space-y-6">
          <Card className="bg-slate-950 text-white border-slate-800 shadow-2xl relative overflow-hidden h-full flex flex-col">
            {/* Grid Background */}
            <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>
            <div className="absolute inset-0 bg-gradient-to-b from-transparent via-slate-950/50 to-slate-950"></div>
            
            <CardHeader className="relative z-10">
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2"><Cpu className="h-5 w-5 text-amber-400" /> Neural Core</span>
                {training ? (
                  <Badge className="bg-amber-500 text-black animate-pulse">TRAINING ACTIVE</Badge>
                ) : (
                  <Badge variant="outline" className="text-slate-400 border-slate-700">IDLE</Badge>
                )}
              </CardTitle>
            </CardHeader>
            
            <CardContent className="relative z-10 flex-1 flex flex-col items-center justify-center space-y-8 p-8">
              {/* Visualizer */}
              <div className="relative h-48 w-48">
                <div className={`absolute inset-0 rounded-full border-4 border-slate-800 ${training ? 'animate-spin-slow border-t-amber-500' : ''}`}></div>
                <div className={`absolute inset-4 rounded-full border-4 border-slate-800 ${training ? 'animate-reverse-spin border-b-blue-500' : ''}`}></div>
                <div className="absolute inset-0 flex items-center justify-center">
                  {voiceType === 'cloned' ? (
                      <Mic className={`h-12 w-12 ${training ? 'text-white animate-pulse' : 'text-slate-700'}`} />
                  ) : (
                      <Bot className={`h-12 w-12 ${training ? 'text-white animate-pulse' : 'text-slate-700'}`} />
                  )}
                </div>
              </div>

              <div className="text-center space-y-2">
                <h3 className="text-xl font-medium">Target: {voiceType === 'cloned' ? "Sales Team Clone" : "Synthetic Agent"}</h3>
                <p className="text-slate-400 text-sm max-w-xs mx-auto">
                  {voiceType === 'cloned' 
                    ? "Analysing pitch, tone, and cadence for 99% similarity." 
                    : "Generating optimized voice for clarity and persuasion."}
                </p>
              </div>

              <Button 
                size="lg" 
                className={`w-full max-w-xs ${training ? 'bg-slate-800 text-slate-400' : 'bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-400 hover:to-orange-500 text-white'}`}
                onClick={startTraining}
                disabled={training || (voiceType === 'cloned' && files.length === 0)}
              >
                {training ? 'Processing Neural Weights...' : 'Start Training Sequence'}
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Right: Persona Library */}
        <div className="lg:col-span-3 space-y-6">
          <Card className="bg-card border-border h-full">
            <CardHeader>
              <CardTitle className="text-sm uppercase tracking-widest text-muted-foreground">Active Personas</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
                {personas.map((persona, i) => (
                    <div key={i} className="p-3 rounded-lg border border-border bg-muted/20 hover:border-amber-500/30 transition-colors group">
                        <div className="flex items-center justify-between mb-2">
                            <span className="font-bold text-sm">{persona.name}</span>
                            <Badge variant="outline" className={`text-[10px] ${persona.voice_type === 'cloned' ? 'text-amber-600 border-amber-200' : 'text-blue-600 border-blue-200'}`}>
                                {persona.voice_type === 'cloned' ? 'Human Clone' : 'Synthetic'}
                            </Badge>
                        </div>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                            <Activity className="h-3 w-3" />
                            <span>Similarity: {persona.stats?.similarity || '98%'}</span>
                        </div>
                        <div className="flex gap-2">
                            <Button size="sm" variant="outline" className="flex-1 text-xs h-7 gap-2" onClick={() => {
                                const audio = new Audio('https://www2.cs.uic.edu/~gupta/media/StarWars3.wav');
                                audio.play();
                                toast.success(`Playing preview for ${persona.name}`);
                            }}>
                                <Play className="h-3 w-3" /> Preview
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-blue-500 hover:text-blue-600 hover:bg-blue-50" onClick={() => openEdit(persona)}>
                                <Edit className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 w-7 p-0 text-red-500 hover:text-red-600 hover:bg-red-50" onClick={() => setDeleteId(persona.id)}>
                                <Trash2 className="h-3 w-3" />
                            </Button>
                        </div>
                    </div>
                ))}
                {personas.length === 0 && (
                    <div className="text-center py-10 text-muted-foreground text-xs italic">
                        No personas trained yet.
                    </div>
                )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this voice persona. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Dialog */}
      <Dialog open={!!editPersona} onOpenChange={() => setEditPersona(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Persona</DialogTitle>
            <DialogDescription>Update the name of your voice agent.</DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input value={editName} onChange={(e) => setEditName(e.target.value)} placeholder="Persona Name" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditPersona(null)}>Cancel</Button>
            <Button onClick={confirmEdit}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default VoiceLab;
